/* eslint-disable array-bracket-newline */
import { useCallback, useEffect, useState } from 'react';
import BigNumber from 'bignumber.js';
import { Web3Provider } from '@ethersproject/providers';
import { Direction } from '.';
import { ICoin } from '../reducers/tradingReducer';
import { getPriceRequest } from './paraswap';

export type estimateSuccessCallbackType = {
  gasCostUSD?: string;
  naturalAmount: BigNumber;
  afterSlippage: number;
  priceRoute?: IPriceRequestResponse;
  quoteNaturalAmount?: BigNumber;
};

interface ITradeEstimateProps {
  network: any;
  base: ICoin;
  quote: ICoin;
  direction: Direction; // 0 - fixed input, 1 - fixed output
  slippage: number; // 0.01 -> 1%
  estimateIndex: number; // to re-estimate price with time
  isOnEVMNetwork: boolean;
  priceRoute?: IPriceRequestResponse;
  onNotify: (input: any) => void;
  onEstimateStart: (base: boolean) => void;
  onEstimateError: () => void;
  onEstimateSuccess: (args: estimateSuccessCallbackType) => void;
}

export interface IPriceRequestResponse {
  blockNumber: number; // 14610714
  contractAddress: string; // "0xDEF171Fe48CF0115B1d80b88dc8eAB59176FEe57"
  contractMethod: string; // "swapOnUniswapV2Fork"
  destAmount: string; // "7583108746013111"
  destDecimals: number; // 18
  destToken: string; // "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2"
  destUSD: string; // "22.6519814007"
  gasCost: string; // "189300"
  gasCostUSD: string; // "62.767164"
  hmac: string; // "3ef9c4f8c3c467258a296f8a252d5aea6a8eb368"
  maxImpactReached: boolean; // false
  network: number; // 1
  partner: string; // "kattana"
  partnerFee: number; // 0
  side: string; // "SELL"
  srcAmount: string; // "10000000000000000000"
  srcDecimals: number; // 18
  srcToken: string; // "0x491e136ff7ff03e6ab097e54734697bb5802fc1c"
  srcUSD: string; // "22.6000000000"
  tokenTransferProxy: string; // "0x216b4b4ba9f3e719726886d34a177484278bfcae"
}

type estimateType = (controller: AbortController) => Promise<void>;

/**
 * Hook which reacts to amounts
 */
const useTradeEstimate = ({
  network,
  base,
  quote,
  direction,
  slippage,
  estimateIndex,
  isOnEVMNetwork,
  priceRoute,
  onNotify,
  onEstimateStart,
  onEstimateError,
  onEstimateSuccess,
}: ITradeEstimateProps) => {
  const [rate, setRate] = useState<BigNumber>(new BigNumber(0));
  const [gasPrice, setGasPrice] = useState<BigNumber>(BigNumber(0));
  const injectedEth = (window as any).ethereum;
  const provider = injectedEth
    ? new Web3Provider((window as any).ethereum)
    : null;

  const estimateBaseQuote: estimateType = async (controller) => {
    onEstimateStart(false);
    const { data, error } = await getPriceRequest({
      base,
      quote,
      network,
      onNotify,
      controller,
    });

    if (error) {
      console.warn(error);
      if (!controller.signal.aborted) {
        onEstimateError();
        setRate(new BigNumber(0));
      }
    } else {
      const { priceRoute }: { priceRoute: IPriceRequestResponse } = data;
      const { destAmount, gasCostUSD } = priceRoute as IPriceRequestResponse;
      const naturalAmount = new BigNumber(destAmount).div(
        10 ** Number(quote.decimals)
      );
      const afterSlippage = (100 - slippage) / 100;

      onEstimateSuccess({
        gasCostUSD,
        naturalAmount,
        afterSlippage,
        priceRoute,
      });

      if (naturalAmount.gt(0) && base.amount.gt(0)) {
        setRate(naturalAmount.div(base.amount));
      }
    }
  };

  /**
   * Effect 1
   * Base->Quote
   */
  useEffect(() => {
    const controller = new AbortController();
    if (direction == Direction.BaseQuote && isOnEVMNetwork && provider) {
      if (base.amount.gt(0)) {
        estimateBaseQuote(controller);
      } else {
        onEstimateError();
      }
    }

    return () => {
      controller.abort();
    };
  }, [base, slippage, network, estimateIndex, isOnEVMNetwork, direction]);

  const estimateQuoteBase: estimateType = async (controller) => {
    onEstimateStart(true);
    const { data, error } = await getPriceRequest({
      base: quote,
      quote: base,
      network,
      onNotify,
      controller,
    });
    if (error) {
      console.warn(error);
      if (!controller.signal.aborted) {
        onEstimateError();
        setRate(new BigNumber(0));
      }
    } else {
      const { priceRoute }: { priceRoute: IPriceRequestResponse } = data;
      const { srcAmount, destAmount, gasCostUSD } =
        priceRoute as IPriceRequestResponse;
      const quoteNaturalAmount = new BigNumber(srcAmount).div(
        10 ** Number(quote.decimals)
      );
      const naturalAmount = new BigNumber(destAmount).div(
        10 ** Number(base.decimals)
      );
      const afterSlippage = (100 - slippage) / 100;

      /**
       * Update rate
       */
      if (quote.amount.gt(0) && naturalAmount.gt(0)) {
        setRate(quote.amount.div(naturalAmount));
      }
      onEstimateStart(false);
      /**
       * So we need a correct price route
       * and do a base estimate
       */
      const { data: baseData, error: baseError } = await getPriceRequest({
        base: {
          ...base,
          amount: naturalAmount,
        },
        quote: {
          ...quote,
          amount: quoteNaturalAmount,
        },
        network,
        onNotify,
        controller,
      });

      if (baseError) {
        if (!controller.signal.aborted) {
          onEstimateError();
        }
      } else {
        onEstimateSuccess({
          gasCostUSD,
          naturalAmount,
          afterSlippage,
          priceRoute: baseData.priceRoute, // base estimate will define correct value
          quoteNaturalAmount,
        });
      }
    }
  };

  /**
   * Effect 2
   * Quote->Base
   */
  useEffect(() => {
    const controller = new AbortController();
    if (direction == Direction.QuoteBase && isOnEVMNetwork && provider) {
      if (quote.amount.gt(0)) {
        estimateQuoteBase(controller);
      } else {
        onEstimateError();
      }
    }

    return () => {
      controller.abort();
    };
  }, [quote, slippage, network, estimateIndex, isOnEVMNetwork, direction]);

  /**
   * Effect 3
   * Update gap price when we have provider and price route
   */
  useEffect(() => {
    let active = true;

    if (provider && priceRoute) {
      provider
        .getGasPrice()
        .then((price) => {
          if (active) {
            setGasPrice(BigNumber(price.toNumber()));
          }
        })
        .catch((e) => console.error(e));
    }

    return () => {
      active = false;
    };
  }, [priceRoute]);

  return {
    rate,
    gasPrice,
  };
};

export default useTradeEstimate;
